/**
 * BannedOverlay — full-screen lockdown shown when the account is banned.
 *
 * Covers the entire app (above all UI) so a banned player can't reach any
 * action; the ONLY control is Sign out. This is the UX half of the ban — the
 * server independently rejects every gameplay/social/purchase action, so even
 * a modified client that hides this overlay can't actually do anything.
 *
 * Body-level singleton. Emits 'signout' when the player taps Sign out.
 * No entrance opacity animation (instant show) by design, so there's no
 * Android first-frame flicker to guard against.
 */

import { EventEmitter } from '../../engine/core/EventEmitter';

interface BannedOverlayEvents {
  'signout': void;
}

const STYLE_ID = 'sdl-banned-styles';

export class BannedOverlay extends EventEmitter<BannedOverlayEvents> {
  public element: HTMLElement;
  private visible = false;

  constructor() {
    super();
    BannedOverlay.injectStyles();

    this.element = document.createElement('div');
    this.element.className = 'sdl-banned';
    this.element.innerHTML = `
      <div class="sdl-banned-panel">
        <div class="sdl-banned-icon">🚫</div>
        <div class="sdl-banned-title">ACCOUNT BANNED</div>
        <div class="sdl-banned-msg">Your account has been banned for breaking our rules. You can't play, chat, or make purchases.</div>
        <button class="sdl-banned-signout" type="button">Sign out</button>
      </div>
    `;
    document.body.appendChild(this.element);

    const btn = this.element.querySelector('.sdl-banned-signout') as HTMLButtonElement;
    btn.addEventListener('click', () => this.emit('signout', undefined));
  }

  show(): void {
    if (this.visible) return;
    this.visible = true;
    this.element.classList.add('visible');
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.element.classList.remove('visible');
  }

  isVisible(): boolean {
    return this.visible;
  }

  private static injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .sdl-banned {
        position: fixed; inset: 0;
        z-index: 9000;
        display: none;
        align-items: center; justify-content: center;
        padding: 24px;
        background: rgba(8, 8, 10, 0.94);
        backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
      }
      .sdl-banned.visible { display: flex; }
      .sdl-banned-panel {
        width: 320px; max-width: 100%;
        padding: 32px 24px 24px;
        background: var(--concrete, #111114);
        border: 1px solid rgba(255, 90, 90, 0.35);
        border-radius: 22px;
        text-align: center;
        box-shadow: 0 20px 60px rgba(0,0,0,0.7);
      }
      .sdl-banned-icon { font-size: 48px; line-height: 1; }
      .sdl-banned-title {
        margin-top: 12px;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 30px; letter-spacing: 2px;
        color: #FF5A5A;
      }
      .sdl-banned-msg {
        margin-top: 10px;
        font-family: 'Rajdhani', sans-serif;
        font-size: 14px; line-height: 1.45;
        color: rgba(255,255,255,0.7);
      }
      .sdl-banned-signout {
        width: 100%;
        margin-top: 22px;
        padding: 14px 0;
        border: none; border-radius: 14px;
        background: linear-gradient(135deg, #FF7A7A, #E23B3B);
        color: #2a0606;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 22px; letter-spacing: 2px;
        cursor: pointer;
        box-shadow: 0 6px 20px rgba(226, 59, 59, 0.3);
        transition: transform 0.1s;
      }
      .sdl-banned-signout:active { transform: scale(0.97); }
    `;
    document.head.appendChild(style);
  }
}
